import { useState } from 'react'
import { Dialog, DialogPanel } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'

import kronospaket from '../src/kronos-certified.png'
import certifiedpassed from '../src/certified-passed.png'
import transport from '../src/transport.png'
import kronosbox from '../src/kronos-box.png'
import urbrev from '../src/urbrev-logo.png'
import certification from '../src/urbrev2.png'

const navigation = [
  { name: 'Kronos köpskydd', href: '/kronos-secure' },
  { name: 'Kronos Certified', href: '/kronos-certified' },
  { name: 'Kontrollera certifiering', href: '/kronos-certified-control' },
  { name: 'Klockvärdering', href: '/klockvardering' },
  { name: 'Företagsannonsering', href: '/foretagsannonsering' },
  { name: 'Marknadsföring', href: '/marknadsforing' },
  { name: 'Blogg', href: '/blogg' },
  { name: 'Integritetspolicy', href: '/integritetspolicy' },
  { name: 'Användarvillkor', href: '/anvandarvillkor' },
]

const processSteps = [
  {
    number: '01',
    title: 'Skapa din annons',
    text: 'Publicera din klockannons på Kronos och notera annonsens ID.',
  },
  {
    number: '02',
    title: 'Beställ certifiering',
    text: 'Gå till UrBrevs beställningssida och ange ditt annons-ID.',
  },
  {
    number: '03',
    title: 'Skicka in klockan',
    text: 'UrBrev skickar en fraktsedel och en speciallåda för transporten.',
  },
  {
    number: '04',
    title: 'Få din certifiering',
    text: 'Efter granskningen får du ett digitalt certifikat kopplat till din annons.',
  },
]

const benefits = [
  {
    title: 'Kontroll av klockan',
    text: 'Klockan öppnas, inspekteras och dokumenteras av UrBrev. Komponenterna verifieras och processen fotograferas.',
    image: certification,
    alt: 'UrBrev granskar och dokumenterar en klocka',
    imageFirst: false,
    eyebrow: '01 · GRANSKNING',
  },
  {
    title: 'Transportförsäkring ingår',
    text: 'Transportförsäkring upp till 150 000 kr ingår när klockan skickas till och från UrBrev för certifiering.',
    image: transport,
    alt: 'Transport i samband med klockcertifiering',
    imageFirst: true,
    eyebrow: '02 · TRANSPORT',
  },
  {
    title: 'Digitalt certifikat och badge',
    text: 'Efter granskningen utfärdas ett digitalt certifikat som kan delas med potentiella köpare. Din annons får även en Kronos Certified-badge.',
    image: certifiedpassed,
    alt: 'Digitalt certifikat för Kronos Certified',
    imageFirst: false,
    eyebrow: '03 · CERTIFIERING',
  },
]

function CheckIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" className="h-5 w-5">
      <path
        d="m5 12.5 4.2 4.2L19 7"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function KronosCertified({ siteLogo }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  return (
    <div className="min-h-screen bg-white text-slate-950 antialiased">
      <header className="fixed inset-x-0 top-0 z-50 bg-white shadow">
        <nav className="flex items-center justify-between p-6 lg:px-8">
          <a href="/" className="flex items-center -m-1.5 p-1.5">
            <span className="sr-only">Krono</span>
            <img alt="Krono Logo" src={siteLogo} className="h-6 w-auto rounded-md" />
          </a>

          <div className="flex lg:hidden">
            <button
              type="button"
              className="p-2.5 text-gray-700 rounded-md"
              onClick={() => setMobileMenuOpen(true)}
            >
              <span className="sr-only">Open menu</span>
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
                />
              </svg>
            </button>
          </div>

          <div className="hidden lg:flex lg:gap-x-8">
            {navigation.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className="text-sm font-semibold text-gray-900 hover:underline"
              >
                {item.name}
              </a>
            ))}
          </div>
        </nav>

        {/* Mobile menu */}
        <Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className="lg:hidden">
          <div className="fixed inset-0 z-50" />
          <DialogPanel className="fixed inset-y-0 right-0 z-50 w-full max-w-sm bg-white px-6 py-6 overflow-y-auto shadow-lg">
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                className="p-2.5 text-gray-700 rounded-md"
              >
                <span className="sr-only">Close menu</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
            <div className="mt-6 space-y-2">
              {navigation.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  className="block rounded-lg px-3 py-2 text-base font-semibold text-gray-900 hover:bg-gray-50"
                >
                  {item.name}
                </a>
              ))}
            </div>
          </DialogPanel>
        </Dialog>
      </header>

      <main>
        <section className="relative isolate overflow-hidden border-b border-slate-100 bg-[#fff]">
          <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 py-16 sm:px-8 sm:py-20 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16 lg:px-10 lg:py-24">
            <div className="max-w-2xl">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-600 shadow-sm">
                Klockcertifiering via Kronos
              </div>
              <h1 className="font-poppins text-4xl font-semibold leading-[1.08] tracking-tight text-slate-950 sm:text-5xl lg:text-6xl">
                Visa vad din klocka <span className="text-[#00a1d6]">verkligen är värd.</span>
              </h1>
              <div className="mt-12 flex flex-col items-center justify-center gap-3 border-t border-slate-200/80 pt-7 sm:flex-row sm:gap-5">
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-600">
                  I samarbete med
                </span>
                <img
                  src={urbrev}
                  alt="UrBrev"
                  className="h-8 w-auto max-w-[150px] object-contain"
                />
              </div>
              <p className="mt-6 max-w-xl text-base leading-8 text-slate-600 sm:text-lg py-8">
                Med Kronos Certified kan du få din klockas äkthet, funktion och skick granskade av
                UrBrev. Certifieringen ger potentiella köpare mer information inför affären.
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a
                  href="https://www.urbrev.se/kronosorder.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center rounded-full bg-[#00a1d6] px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-blue-900/10 transition hover:-translate-y-0.5 hover:bg-[#0088b3]"
                >
                  Beställ certifiering{' '}
                  <span aria-hidden="true" className="ml-2 text-base">
                    ↗
                  </span>
                </a>
                <a
                  href="#sa-fungerar-det"
                  className="inline-flex items-center justify-center rounded-full border border-slate-300 bg-white px-6 py-3.5 text-sm font-semibold text-slate-900 transition hover:border-slate-500 hover:bg-slate-50"
                >
                  Så fungerar det{' '}
                  <span aria-hidden="true" className="ml-2">
                    ↓
                  </span>
                </a>
              </div>

              <div className="mt-10 flex flex-col gap-4 border-t border-slate-200 pt-6 sm:flex-row sm:items-center sm:gap-6">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-red-700 ring-1 ring-slate-200">
                    <CheckIcon />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Digitalt certifikat</p>
                    <p className="mt-0.5 text-xs text-slate-500">Kopplat till din annons</p>
                  </div>
                </div>
                <div className="hidden h-9 w-px bg-slate-200 sm:block" />
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-red-700 ring-1 ring-slate-200">
                    <CheckIcon />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Försäkrad transport</p>
                    <p className="mt-0.5 text-xs text-slate-500">Upp till 150 000 kr</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative mx-auto w-full max-w-xl">
              <div className="absolute inset-5 rounded-[2rem] bg-slate-200/70 blur-2xl" />
              <div className="relative overflow-hidden rounded-[1.75rem] border border-white bg-white p-3 shadow-2xl shadow-slate-900/10">
                <img
                  src={certifiedpassed}
                  alt="Kronos Certified certifieringspaket från UrBrev"
                  className="h-[300px] w-full rounded-[1.25rem] object-cover sm:h-[390px]"
                />
                <div className="flex items-center justify-between gap-4 px-3 py-4 sm:px-5 sm:py-5">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#00a1d6]">
                      Kronos Certified
                    </p>
                    <p className="mt-1 text-lg font-semibold tracking-tight text-slate-950">
                      Dokumenterad klockkontroll
                    </p>
                  </div>
                  <img src={urbrev} alt="UrBrev" className="h-7 max-w-[105px] object-contain" />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-20 lg:px-10 lg:py-24">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00a1d6]">
              Trygghet som syns
            </p>
            <h2 className="mt-4 font-poppins text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
              En tydligare bild av klockans skick
            </h2>
            <p className="mt-5 text-base leading-7 text-slate-600">
              Kronos Certified samlar granskning, dokumentation och ett digitalt certifikat i en
              process som gör informationen lättare att dela med spekulanter.
            </p>
          </div>

          <div className="mt-12 grid gap-4 md:grid-cols-3">
            {[
              {
                number: '01',
                title: 'Granskad',
                text: 'Klockan öppnas, inspekteras och dokumenteras.',
              },
              {
                number: '02',
                title: 'Dokumenterad',
                text: 'Kontrollen fotograferas för ökad transparens.',
              },
              {
                number: '03',
                title: 'Certifierad',
                text: 'Digitalt certifikat och badge kopplas till annonsen.',
              },
            ].map((item) => (
              <div
                key={item.number}
                className="group rounded-2xl border border-slate-200 bg-white p-6 transition hover:-translate-y-1 hover:border-slate-300 hover:shadow-lg sm:p-7"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold tracking-[0.18em] text-[#00a1d6]">
                    {item.number}
                  </span>
                  <span className="h-px w-12 bg-slate-200 transition group-hover:w-16 group-hover:bg-[#00a1d6]" />
                </div>
                <h3 className="mt-7 text-xl font-semibold tracking-tight text-slate-950">
                  {item.title}
                </h3>
                <p className="mt-3 text-sm leading-6 text-slate-600">{item.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section
          id="sa-fungerar-det"
          className="scroll-mt-24 bg-slate-950 py-16 text-white sm:py-20 lg:py-24"
        >
          <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10">
            <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
              <div className="max-w-lg">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00a1d6]">
                  En enkel process
                </p>
                <h2 className="mt-4 font-poppins text-3xl font-semibold tracking-tight sm:text-4xl">
                  Så fungerar beställningen
                </h2>
                <p className="mt-5 leading-7 text-slate-300">
                  Du startar beställningen hos UrBrev efter att du har skapat din annons på Kronos.
                  Följ stegen för att skicka in klockan till granskning.
                </p>
                <a
                  href="https://www.urbrev.se/kronosorder.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-7 inline-flex items-center rounded-full bg-white px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-red-50"
                >
                  Beställ hos UrBrev{' '}
                  <span aria-hidden="true" className="ml-2">
                    ↗
                  </span>
                </a>
              </div>
              <div className="divide-y divide-white/15 border-y border-white/15">
                {processSteps.map((step) => (
                  <div
                    key={step.number}
                    className="grid gap-3 py-6 sm:grid-cols-[64px_1fr] sm:gap-5 sm:py-7"
                  >
                    <p className="font-poppins text-2xl font-semibold text-[#00a1d6]">
                      {step.number}
                    </p>
                    <div>
                      <h3 className="text-lg font-semibold text-white">{step.title}</h3>
                      <p className="mt-2 max-w-xl text-sm leading-6 text-slate-300">{step.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl space-y-6 px-5 py-16 sm:space-y-8 sm:px-8 sm:py-20 lg:px-10 lg:py-24">
          {benefits.map((benefit) => (
            <article
              key={benefit.eyebrow}
              className="grid overflow-hidden rounded-[1.5rem] border border-slate-200 bg-white md:min-h-[340px] md:grid-cols-2"
            >
              <div
                className={`relative min-h-[240px] bg-slate-100 ${benefit.imageFirst ? 'md:order-2' : ''}`}
              >
                <img
                  src={benefit.image}
                  alt={benefit.alt}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </div>
              <div className="flex flex-col justify-center p-7 sm:p-10 lg:p-12">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#00a1d6]">
                  {benefit.eyebrow}
                </p>
                <h2 className="mt-4 font-poppins text-2xl font-semibold tracking-tight text-slate-950 sm:text-3xl">
                  {benefit.title}
                </h2>
                <p className="mt-4 max-w-xl text-sm leading-7 text-slate-600 sm:text-base">
                  {benefit.text}
                </p>
              </div>
            </article>
          ))}

          <article className="grid overflow-hidden rounded-[1.5rem] border border-slate-200 bg-[#fafafa] md:grid-cols-2">
            <div className="flex flex-col justify-center p-7 sm:p-10 lg:p-12">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#00a1d6]">
                Beställning
              </p>
              <h2 className="mt-4 font-poppins text-2xl font-semibold tracking-tight text-slate-950 sm:text-3xl">
                Från Kronos annons till UrBrev
              </h2>
              <p className="mt-4 text-sm leading-7 text-slate-600 sm:text-base">
                När du har skapat en annons på Kronos beställer du certifieringen på UrBrevs
                webbplats. Ange annons-ID:t så kan beställningen kopplas till rätt annons.
              </p>
              <a
                href="https://www.urbrev.se/kronosorder.html"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex w-fit items-center rounded-full bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-red-700"
              >
                Till beställningen{' '}
                <span aria-hidden="true" className="ml-2">
                  ↗
                </span>
              </a>
            </div>
            <div className="min-h-[240px] bg-white p-6 sm:p-10">
              <img
                src={kronosbox}
                alt="Kronos certifieringslåda för transport till UrBrev"
                loading="lazy"
                className="h-full max-h-[340px] w-full rounded-xl object-contain"
              />
            </div>
          </article>
        </section>

        <section className="border-t border-slate-200 bg-[#fafafa] px-5 py-16 sm:px-8 sm:py-20 lg:px-10">
          <div className="mx-auto max-w-5xl rounded-[1.75rem] bg-white px-6 py-10 text-center shadow-sm ring-1 ring-slate-200 sm:px-12 sm:py-14">
            <img src={urbrev} alt="UrBrev" className="mx-auto h-8 max-w-[150px] object-contain" />
            <p className="mt-6 text-xs font-bold uppercase tracking-[0.2em] text-[#00a1d6]">
              Kronos Certified i samarbete med UrBrev
            </p>
            <h2 className="mx-auto mt-4 max-w-2xl font-poppins text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
              Ge köpare mer information inför nästa klockaffär
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-base leading-7 text-slate-600">
              Beställ en certifiering och koppla resultatet till din annons på Kronos.
            </p>
            <a
              href="https://www.urbrev.se/kronosorder.html"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-7 inline-flex items-center justify-center rounded-full bg-[#00a1d6] px-6 py-3.5 text-sm font-semibold text-white transition hover:bg-[#0088b3]"
            >
              Beställ certifiering{' '}
              <span aria-hidden="true" className="ml-2">
                ↗
              </span>
            </a>
          </div>
          <div className="mx-auto mt-12 max-w-5xl text-center">
            <p className="text-lg font-semibold text-slate-900">
              Har du frågor om Kronos Certified?
            </p>
            <a
              href="mailto:info@kronos.se"
              className="mt-2 inline-block text-sm font-semibold text-[#00a1d6] underline decoration-[#00a1d6] underline-offset-4 hover:decoration-[#0088b3]"
            >
              info@kronos.se
            </a>
          </div>
        </section>
      </main>
    </div>
  )
}

export default KronosCertified
